import { useState, useEffect } from 'react';
import { getModuleDefaults, hasModulePreferences } from 'config/preferences';
import { syncAllPreferencesToDB } from 'utils/preferencesSync';

// ----------------------------------------------------------------------

/**
 * Hook for managing module-based preferences
 * Uses centralized configuration from config/preferences.js
 * 
 * Example:
 * {
 *   "Clients": { "Status": "active" }
 * }
 */

const useModulePreferences = (moduleKey) => {
  const moduleHasPreferences = hasModulePreferences(moduleKey);
  const defaultPrefs = moduleHasPreferences ? getModuleDefaults(moduleKey) : {};

  const [preferences, setPreferences] = useState(() => {
    if (!moduleHasPreferences) {
      return {};
    }

    // Try to load from sessionStorage immediately during initialization
    try {
      const stored = sessionStorage.getItem('prefs');
      if (stored) {
        const allPrefs = JSON.parse(stored);
        if (allPrefs[moduleKey]) {
          // Apply font size to <html> element immediately if this is Global module
          if (moduleKey === 'Global' && allPrefs[moduleKey].FontSize) {
            document.documentElement.style.fontSize = `${allPrefs[moduleKey].FontSize}px`;
          }
          return allPrefs[moduleKey];
        }
      }
    } catch (error) {
      console.error('Error loading module preferences:', error);
    }
    
    // Apply default font size to <html> element if this is Global module
    if (moduleKey === 'Global' && defaultPrefs.FontSize) {
      document.documentElement.style.fontSize = `${defaultPrefs.FontSize}px`;
    }
    
    return defaultPrefs;
  });

  // Track if preferences have been loaded from sessionStorage
  const [isLoaded, setIsLoaded] = useState(false);

  const applyModulePreferences = (allPrefs) => {
    if (!moduleHasPreferences || !allPrefs?.[moduleKey]) {
      return;
    }

    setPreferences(allPrefs[moduleKey]);

    if (moduleKey === 'Global' && allPrefs[moduleKey].FontSize) {
      document.documentElement.style.fontSize = `${allPrefs[moduleKey].FontSize}px`;
    }
  };

  // Load preferences from sessionStorage on mount and when async DB load completes
  useEffect(() => {
    if (!moduleHasPreferences) {
      console.warn(`Module "${moduleKey}" has no preferences configured in config/preferences.js`);
      setIsLoaded(true);
      return;
    }

    const loadFromSessionStorage = () => {
      const stored = sessionStorage.getItem('prefs');
      if (stored) {
        try {
          applyModulePreferences(JSON.parse(stored));
        } catch (error) {
          console.error('Error loading module preferences:', error);
        }
      }
      setIsLoaded(true);
    };

    const handlePreferencesLoaded = (event) => {
      applyModulePreferences(event.detail);
      setIsLoaded(true);
    };

    loadFromSessionStorage();
    window.addEventListener('preferencesLoaded', handlePreferencesLoaded);

    return () => {
      window.removeEventListener('preferencesLoaded', handlePreferencesLoaded);
    };
  }, [moduleHasPreferences, moduleKey]);

  // Function to update preferences and save to sessionStorage + sync to DB
  const updatePreferences = async (newPrefs) => {
    if (!moduleHasPreferences) {
      return;
    }

    const updatedPrefs = { ...preferences, ...newPrefs };
    setPreferences(updatedPrefs);
    
    // Apply font size to <html> element immediately if this is Global module and FontSize changed
    if (moduleKey === 'Global' && newPrefs.FontSize) {
      document.documentElement.style.fontSize = `${newPrefs.FontSize}px`;
    }
    
    // Save to sessionStorage immediately for fast access
    const stored = sessionStorage.getItem('prefs');
    let allPrefs = {};
    if (stored) {
      try {
        allPrefs = JSON.parse(stored);
      } catch (error) {
        console.error('Error parsing stored preferences:', error);
      }
    }
    
    allPrefs[moduleKey] = updatedPrefs;
    sessionStorage.setItem('prefs', JSON.stringify(allPrefs));
    
    // Sync all preferences to database in background
    try {
      await syncAllPreferencesToDB();
    } catch (error) {
      console.error('Error syncing preferences to database:', error);
    }
  };

  // Function to update a specific key within the module
  const updatePreference = async (key, value) => {
    if (!moduleHasPreferences) {
      return;
    }

    // Session-only preferences should not sync to DB.
    const isSessionOnlyPreference =
      (moduleKey === 'Divisions' && key === 'Client') ||
      (moduleKey === 'FeeGuides' && key === 'PaymentType');

    if (isSessionOnlyPreference) {
      // Update preferences without syncing to DB
      const updatedPrefs = { ...preferences, [key]: value };
      setPreferences(updatedPrefs);
      
      // Save to sessionStorage only
      const stored = sessionStorage.getItem('prefs');
      let allPrefs = {};
      if (stored) {
        try {
          allPrefs = JSON.parse(stored);
        } catch (error) {
          console.error('Error parsing stored preferences:', error);
        }
      }
      
      allPrefs[moduleKey] = updatedPrefs;
      sessionStorage.setItem('prefs', JSON.stringify(allPrefs));
      
      return; // Skip DB sync
    }
    
    // Normal update with DB sync for all other cases
    await updatePreferences({ [key]: value });
  };

  // Function to get a specific preference value
  const getPreference = (key, defaultValue = null) => {
    return preferences[key] !== undefined ? preferences[key] : defaultValue;
  };

  return [preferences, updatePreferences, updatePreference, getPreference, isLoaded];
};

export default useModulePreferences;
